import React, { useState } from "react";
import {
  Send,
  CheckCircle,
  AlertTriangle,
  Loader2,
  User,
  Phone,
  Mail,
  MessageSquare
} from "lucide-react";

const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");

const ContactForm = () => {
  // ✅ UPDATED CONTACT EMAIL (display only — Netlify form notifications are set in Netlify > Forms)
  const contactEmail = "info@agentletouchhomecarellc1.com";
  const formName = "contact";

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
    "bot-field": "" // honeypot must exist as a real field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [statusMsg, setStatusMsg] = useState("");

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setStatus("error");
      setStatusMsg("Please fix the highlighted fields and try again.");
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    // Clear status banner while typing
    if (status !== "idle") {
      setStatus("idle");
      setStatusMsg("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setStatus("submitting");
    setStatusMsg("Sending your request…");

    try {
      // ✅ Post to current route (better for SPAs on Netlify)
      const response = await fetch(window.location.pathname, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": formName,
          ...formData
        })
      });

      // Netlify sometimes returns 200 or a redirect (302)
      if (!response.ok && response.status !== 302) {
        throw new Error(`Netlify submission failed: ${response.status}`);
      }

      setStatus("success");
      setStatusMsg("✅ Thanks! Your message was sent. We’ll reach out shortly.");

      setFormData({
        fullName: "",
        phone: "",
        email: "",
        message: "",
        "bot-field": ""
      });

      setErrors({});
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus("error");
      setStatusMsg("Sorry — something went wrong. Please try again in a moment.");
    }
  };

  const isSubmitting = status === "submitting";

  return (
    <div className="w-full">
      {/* ✅ Netlify detection stub (CRITICAL for React apps) */}
      <form name={formName} data-netlify="true" netlify-honeypot="bot-field" hidden>
        <input type="text" name="fullName" />
        <input type="tel" name="phone" />
        <input type="email" name="email" />
        <textarea name="message" />
        <input name="bot-field" />
      </form>

      {/* Contact Info */}
      <div className="mb-6 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4" style={{ color: "#008080" }} />
          <a href="tel:16149284169" className="underline" style={{ color: "#008080" }}>
            614.928.4169
          </a>
        </div>

        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" style={{ color: "#008080" }} />
          <a href={`mailto:${contactEmail}`} className="underline" style={{ color: "#008080" }}>
            {contactEmail}
          </a>
        </div>
      </div>

      {/* ✅ Banner message that shows on submit (sending/success/error) */}
      {status !== "idle" && statusMsg && (
        <div
          className="mb-5 rounded-xl border p-4 text-sm flex items-start gap-3"
          style={{
            borderColor:
              status === "success" ? "#008080" : status === "error" ? "#fca5a5" : "#e5e7eb",
            background:
              status === "success"
                ? "rgba(0,128,128,0.08)"
                : status === "error"
                ? "rgba(239,68,68,0.06)"
                : "rgba(0,0,0,0.02)"
          }}
          role="status"
          aria-live="polite"
        >
          {status === "success" ? (
            <CheckCircle className="w-5 h-5 mt-0.5" style={{ color: "#008080" }} />
          ) : status === "error" ? (
            <AlertTriangle className="w-5 h-5 mt-0.5 text-red-500" />
          ) : (
            <Loader2 className="w-5 h-5 mt-0.5 animate-spin" style={{ color: "#008080" }} />
          )}

          <div className="flex-1">{statusMsg}</div>

          {(status === "success" || status === "error") && (
            <button
              type="button"
              onClick={() => {
                setStatus("idle");
                setStatusMsg("");
              }}
              className="text-xs underline"
              style={{ color: "#008080" }}
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      <form
        name={formName}
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Required for Netlify */}
        <input type="hidden" name="form-name" value={formName} />

        {/* ✅ Honeypot must be a REAL field named bot-field (not just hidden input) */}
        <p className="hidden">
          <label>
            Don’t fill this out:{" "}
            <input name="bot-field" value={formData["bot-field"]} onChange={handleChange} />
          </label>
        </p>

        <div>
          <label htmlFor="fullName" className="form-label flex items-center gap-2">
            <User className="w-4 h-4" style={{ color: "#008080" }} />
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={`form-input ${errors.fullName ? "border-red-400" : ""}`}
            placeholder="Enter your full name"
            autoComplete="name"
          />
          {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="form-label flex items-center gap-2">
            <Phone className="w-4 h-4" style={{ color: "#008080" }} />
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`form-input ${errors.phone ? "border-red-400" : ""}`}
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        <div>
          <label htmlFor="email" className="form-label flex items-center gap-2">
            <Mail className="w-4 h-4" style={{ color: "#008080" }} />
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`form-input ${errors.email ? "border-red-400" : ""}`}
            placeholder="your.email@example.com"
            autoComplete="email"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="message" className="form-label flex items-center gap-2">
            <MessageSquare className="w-4 h-4" style={{ color: "#008080" }} />
            How Can We Help?
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="form-input resize-none"
            placeholder="Tell us about your care needs..."
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Request Care Consultation
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;

